
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Snackbar,
    Alert,
    IconButton,
    Tooltip,
    Box,
    CircularProgress,
} from "@mui/material";
import { Edit, Delete, Save, Close, FileCopy } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection } from 'components/v2/record/DetailSection';

const ClickableField = ({ label, value, onClick }) => {
    const display = value || '-';
    const canClick = Boolean(onClick && value);
    return (
        <MDBox>
            <MDTypography variant="button" fontWeight="regular" color="text">
                <strong>{label}:</strong>
            </MDTypography>
            <MDTypography
                component="span"
                variant="button"
                color={canClick ? 'info' : 'text'}
                fontWeight={canClick ? 'medium' : 'regular'}
                sx={{ cursor: canClick ? 'pointer' : 'default', ml: 1 }}
                onClick={canClick ? onClick : undefined}
            >
                {display}
            </MDTypography>
        </MDBox>
    );
};

const MerchantAssetInventoryDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const { canEdit, canDelete, canCreate } = usePermission('merchant_asset_inventories');
    const [inventory, setInventory] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [isEditLoading, setIsEditLoading] = useState(false);
    const [isCloneLoading, setIsCloneLoading] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchInventoryData();
    }, [id]);

    const fetchInventoryData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/merchant-asset-inventories/${id}`);
            setInventory(response.data);
        } catch (error) {
            console.error('Error fetching inventory data:', error);
            setInventory(null);
            setSnackbar({ open: true, message: 'Error loading inventory details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else if (userName) {
            setSnackbar({ open: true, message: `User profile unavailable for ${userName}`, severity: 'info' });
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setInventory(prev => ({ ...prev, [field]: value }));
    };

    const handleEditToggle = () => {
        setIsEditLoading(true);
        setIsEditing(true);
        setIsEditLoading(false);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setUpdatedFields({});
        fetchInventoryData();
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        setIsSaveLoading(true);
        try {
            await axiosInstance.put(`/merchant-asset-inventories/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Inventory updated successfully', severity: 'success' });
            fetchInventoryData();
        } catch (error) {
            console.error('Error updating inventory:', error);
            const detail = error.response?.data?.detail;
            setSnackbar({
                open: true,
                message: typeof detail === 'string' ? detail : 'Error updating inventory',
                severity: 'error',
            });
        } finally {
            setIsSaveLoading(false);
        }
    };

    const handleClone = async () => {
        if (!inventory) return;
        setIsCloneLoading(true);
        try {
            const payload = {
                merchant_asset_id: inventory.merchant_asset_id,
                date: inventory.date,
                time_slot: inventory.time_slot || null,
                qty_total: inventory.qty_total,
                available_units: inventory.available_units,
                total_capacity: inventory.total_capacity,
                available_capacity: inventory.available_capacity,
                status: inventory.status || 'Available',
                occupancy_status: inventory.occupancy_status || 'Empty',
                override_price: inventory.override_price,
                qty_contracted: inventory.qty_contracted || 0,
                qty_freesell: inventory.qty_freesell || 0,
                qty_reserved: inventory.qty_reserved || 0,
                release_date: inventory.release_date || null,
                blackout: Boolean(inventory.blackout),
            };
            // Avoid unique constraint on same asset+date+slot by clearing slot if set
            if (payload.time_slot) {
                payload.time_slot = null;
            } else {
                // bump date by 1 day when no slot to clone into a free key
                const d = new Date(payload.date);
                d.setDate(d.getDate() + 1);
                payload.date = d.toISOString().slice(0, 10);
            }
            const response = await axiosInstance.post('/merchant-asset-inventories/', payload);
            setSnackbar({ open: true, message: 'Inventory cloned successfully', severity: 'success' });
            navigate(`/merchant-asset-inventory/${response.data.id}`);
        } catch (error) {
            console.error('Error cloning inventory:', error);
            const detail = error.response?.data?.detail;
            setSnackbar({
                open: true,
                message: typeof detail === 'string' ? detail : 'Error cloning inventory',
                severity: 'error',
            });
        } finally {
            setIsCloneLoading(false);
        }
    };

    const handleConfirmDelete = async () => {
        setIsDeleteLoading(true);
        try {
            await axiosInstance.delete(`/merchant-asset-inventories/${id}`);
            setSnackbar({ open: true, message: 'Inventory deleted successfully', severity: 'success' });
            setTimeout(() => navigate('/merchant-asset-inventories'), 800);
        } catch (error) {
            console.error('Error deleting inventory:', error);
            setSnackbar({ open: true, message: 'Error deleting inventory', severity: 'error' });
            throw error;
        } finally {
            setIsDeleteLoading(false);
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} md={6} key={item.field}>
                        {['created_by_name', 'updated_by_name'].includes(item.field) ? (
                            <ClickableField
                                label={item.label}
                                value={inventory?.[item.field]}
                                onClick={() =>
                                    handleUserClick(
                                        item.field === 'created_by_name'
                                            ? inventory?.created_by
                                            : inventory?.updated_by,
                                        inventory?.[item.field]
                                    )
                                }
                            />
                        ) : item.clickable ? (
                            <ClickableField
                                label={item.label}
                                value={item.renderValue
                                    ? item.renderValue(inventory?.[item.field])
                                    : inventory?.[item.field]}
                                onClick={item.onClick}
                            />
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={inventory?.[item.field]}
                                options={item.options || null}
                                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                                isEditing={isEditing && !item.readOnly}
                                readOnly={item.readOnly || false}
                                type={item.type || 'text'}
                                renderValue={item.renderValue || ((val) => (
                                    val === 0 || val ? String(val) : '-'
                                ))}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <DataLoadingSpinner
                    entityType="default"
                    title="Loading Inventory"
                    message="Please wait while we fetch the merchant asset inventory details..."
                />
            </DashboardLayout>
        );
    }

    if (!inventory) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>Inventory not found</MDTypography>
                    <MDButton
                        sx={{ mt: 2 }}
                        variant="gradient"
                        color="info"
                        onClick={() => navigate('/merchant-asset-inventories')}
                    >
                        Back to list
                    </MDButton>
                </MDBox>
            </DashboardLayout>
        );
    }

    const assetLabel =
        inventory.merchant_asset_name
        || (inventory.merchant_asset_id ? `Asset #${inventory.merchant_asset_id}` : '-');

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        Merchant Asset Inventory #{inventory.id}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Inventory Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox>
                                    {!isEditing ? (
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            {canEdit && (
                                                <Tooltip title="Edit Inventory">
                                                    <IconButton onClick={handleEditToggle} sx={commonIconButtonStyles}>
                                                        {isEditLoading
                                                            ? <CircularProgress size={20} color="inherit" />
                                                            : <Edit sx={commonIconStyles} />}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canCreate && (
                                                <Tooltip title="Clone Inventory">
                                                    <IconButton onClick={handleClone} sx={commonIconButtonStyles}>
                                                        {isCloneLoading
                                                            ? <CircularProgress size={20} color="inherit" />
                                                            : <FileCopy sx={commonIconStyles} />}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canDelete && (
                                                <Tooltip title="Delete Inventory">
                                                    <IconButton
                                                        onClick={() => setIsDeleteModalOpen(true)}
                                                        sx={commonIconButtonStyles}
                                                    >
                                                        {isDeleteLoading
                                                            ? <CircularProgress size={20} color="inherit" />
                                                            : <Delete sx={commonIconStyles} />}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <MDButton
                                                variant="contained"
                                                onClick={handleCancelEdit}
                                                sx={{ background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)', color: '#ffffff' }}
                                                startIcon={<Close />}
                                            >
                                                Cancel
                                            </MDButton>
                                            <MDButton
                                                variant="contained"
                                                onClick={handleSaveChanges}
                                                sx={{ background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)', color: '#ffffff' }}
                                                startIcon={isSaveLoading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                                disabled={isSaveLoading}
                                            >
                                                Save
                                            </MDButton>
                                        </Box>
                                    )}
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", [
                                    { label: "Inventory ID", field: "id", readOnly: true },
                                    {
                                        label: "Merchant Asset",
                                        field: "merchant_asset_id",
                                        clickable: true,
                                        onClick: () => {
                                            if (inventory.merchant_asset_id) {
                                                navigate(`/merchant-assets/${inventory.merchant_asset_id}`);
                                            }
                                        },
                                        renderValue: () => assetLabel,
                                    },
                                    { label: "Departure Date", field: "date", readOnly: true, renderValue: (val) => val ? formatDate(val, 'date') : '-' },
                                    { label: "Time Slot", field: "time_slot", type: "time" },
                                    { label: "Total Units", field: "qty_total" },
                                    { label: "Available Units", field: "available_units" },
                                    { label: "Total Capacity", field: "total_capacity" },
                                    { label: "Available Capacity", field: "available_capacity" },
                                    { label: "Status", field: "status", options: ["Available", "Sold Out", "Unavailable"] },
                                    { label: "Occupancy Status", field: "occupancy_status", options: ["Empty", "Partially Booked", "Fully Booked"] },
                                    { label: "Override Price", field: "override_price" },
                                    { label: "Unavailable (Blackout)", field: "blackout", options: [{ value: true, label: "Yes" }, { value: false, label: "No" }], renderValue: (val) => (val ? "Yes" : "No") },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name", readOnly: true },
                                    { label: "Modified By", field: "updated_by_name", readOnly: true },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <ConfirmDeleteModal
                open={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Merchant Asset Inventory"
                message={`Are you sure you want to delete inventory #${inventory.id}? This cannot be undone.`}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default MerchantAssetInventoryDetail;
